import { Link } from "react-router-dom";
import { getCategory, getProduct, imageSrc } from "@/lib/catalog";

import { useEffect, useRef, useState } from "react";
import { patternBg } from "@/lib/patternBg";
import { Reveal, useReducedMotion } from "./motion";

// Galeria editorial: uma fotografia grande e quatro mais pequenas, lado a lado, sem molduras. Cada fotografia leva à peça (toda a imagem é clicável).
// Cada quadro roda, devagar, pelas fotografias reais da respetiva pasta (conjuntos, porta-chupetas, babetes, tapa-fraldas e saquinhos).
const ids = (prefix, n) => Array.from({ length: n }, (_, i) => `static:${prefix}${i + 1}`);
const BIG = {
  key: "conjuntos", to: "/loja", label: "Para o dia a dia", note: "Conjuntos Babybib", alt: "Conjuntos Babybib: peças a combinar entre si",
  photos: ["static:cj1", "static:cj2", "static:pb1", "static:p10", "static:pb2", "static:p24", "static:tf3", "static:p21", "static:pb3", "static:cj3", "static:p28", "static:tf4", "static:p36", "static:pb4", "static:p17", "static:tf5", "static:co5"],
};
const SMALL = [
  { key: "porta-chupetas", cat: "porta-chupetas", label: "Porta-chupetas", alt: "Porta-chupetas em forma de ursinho, em vários tecidos e com nome", photos: [...ids("pc", 14).slice(0, 5), "static:p32", ...ids("pc", 14).slice(5, 10), "static:p45", ...ids("pc", 14).slice(10)], start: 1400 },
  { key: "babetes", cat: "babetes", label: "Babetes", alt: "Babetes flor e lenço, em vários tecidos", photos: [...ids("bf", 5), "static:p43", ...ids("bl", 3), "static:p40", "static:p26"], start: 2800 },
  { key: "tapa-fraldas", cat: "tapa-fraldas", label: "Tapa-fraldas", alt: "Tapa-fraldas em musselina e tecidos bordados", photos: [...ids("tf", 2), "static:p17", "static:p24", "static:tapa-fraldas-solo"], start: 4200 },
  { key: "coelhinhos", cat: "coelhinhos", label: "Coelhinhos", alt: "Coelhinho de peluche com orelhas e gola em tecido", photos: [...ids("co", 5), "static:p20", "static:p56"], start: 700 },
  { key: "saquinhos", cat: "saquinhos", label: "Saquinhos de roupa", alt: "Saquinhos de roupa com forro em tecido e nome", photos: [...ids("sq", 3), "static:p44"], start: 5600 },
];
// Lacinhos: as fotografias vêm da própria peça (as que a Alexandra carregou no painel); sem a peça publicada, o quadro não aparece.
const lacinhos = () => {
  const photos = (getProduct("lacinho")?.images ?? []).map((i) => i.id);
  return photos.length ? { key: "lacinhos", cat: "lacinhos", label: "Lacinhos", alt: "Lacinhos em tecido, em vários padrões", photos, start: 2100 } : null;
};
const PERIOD = 6500;

// Roda pelas fotografias: a seguinte aparece por cima da atual (fundido) e fica pré-carregada; cada quadro arranca em momentos diferentes.
function Slides({ t, sizes }) {
  const reduced = useReducedMotion();
  const [i, setI] = useState(0);
  const [prev, setPrev] = useState(null);
  const cur = useRef(0);
  const n = t.photos.length;
  useEffect(() => {
    if (reduced || n < 2) return undefined;
    let timer;
    const tick = () => {
      const next = (cur.current + 1) % n;
      setPrev(cur.current);
      cur.current = next;
      setI(next);
      const img = new Image();
      img.src = imageSrc(t.photos[(next + 1) % n], 1200);
    };
    const first = setTimeout(() => { tick(); timer = setInterval(tick, PERIOD); }, PERIOD + (t.start ?? 0));
    return () => { clearTimeout(first); clearInterval(timer); };
  }, [reduced, n, t.photos, t.start]);
  const show = prev !== null && prev !== i ? [prev, i] : [i];
  return show.map((k) => (
    <img
      key={t.photos[k]}
      src={imageSrc(t.photos[k], 1200)}
      srcSet={`${imageSrc(t.photos[k], 400)} 400w, ${imageSrc(t.photos[k], 1200)} 1200w`}
      sizes={sizes}
      alt={k === i ? t.alt : ""}
      aria-hidden={k !== i}
      loading={k === 0 ? "lazy" : "eager"}
      className={`absolute inset-0 h-full w-full object-cover ${k === i && prev !== null ? "bb-fade-in" : ""}`}
    />
  ));
}

function Tile({ t, sizes, className = "" }) {
  const to = t.to ?? (t.cat && getCategory(t.cat) ? `/loja?categoria=${t.cat}` : "/loja");
  return (
    <Link to={to} data-testid={`home-tile-${t.key}`} className={`group relative block overflow-hidden bg-linen ${className}`}>
      <Slides t={t} sizes={sizes} />
      <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[rgba(43,36,32,.5)] to-transparent" />
      <span className="absolute inset-x-0 bottom-0 p-3 text-ivory sm:p-4">
        <span className="block font-display text-[15px] leading-tight sm:text-lg">{t.label}</span>
        {t.note && <span className="mt-0.5 block text-[11px] uppercase tracking-[0.14em] text-ivory/85">{t.note}</span>}
      </span>
    </Link>
  );
}

export function EditorialGallery() {
  const small = [...SMALL, lacinhos()].filter(Boolean);
  const six = small.length === 6; // 6 quadros: duas filas de três (com 5, um quadro alto em mobile e 2 + 3 em ecrã grande)
  return (
    <section id="pecas" className="-mt-[30px] bg-ivory pb-10 pt-[calc(1.75rem+30px)] sm:pb-14 sm:pt-[calc(3.5rem+30px)] lg:pt-[calc(4rem+30px)]" style={patternBg("vichy-bege", "250,247,242", 0.86, 240)} data-testid="home-pieces">
      <Reveal y={14} className="bb-container mb-4 flex flex-wrap items-end justify-between gap-x-6 sm:mb-7">
        <h2 className="bb-h2 text-ink" style={{ fontSize: "clamp(1.5rem, 3.2vw, 2.6rem)", lineHeight: 1.08 }}>
          Escolhe a tua peça <em className="bb-accent text-bordeaux">da Babybib</em> <span aria-hidden="true" className="text-rose">♡</span>
        </h2>
        <Link to="/loja" data-testid="home-pieces-all" className="bb-link bb-tap text-sm font-medium">Ver todas as peças</Link>
      </Reveal>
      <div className="mx-auto grid grid-cols-1 max-w-[1440px] gap-[3px] sm:gap-1.5 sm:px-8 lg:grid-cols-2">
        <Tile t={BIG} sizes="(min-width: 1024px) 50vw, 100vw" className="aspect-[4/3] sm:aspect-[5/4] lg:aspect-auto lg:h-[min(64vh,620px)]" />
        <div className="grid grid-cols-2 gap-[3px] sm:gap-1.5 lg:h-[min(64vh,620px)] lg:grid-cols-6 lg:grid-rows-2">
          {small.map((t, k) => <Tile key={t.key} t={t} sizes="(min-width: 1024px) 25vw, 50vw" className={`aspect-square lg:aspect-auto ${six || k >= 2 ? "lg:col-span-2" : "lg:col-span-3"} ${!six && k === 0 ? "max-lg:row-span-2 max-lg:aspect-auto" : ""}`} />)}
          </div>
      </div>
    </section>
  );
}
